// useBulkPolicyIntake — client orchestration for R0 Bulk Policy Intake.
//
// Contract (byte-verified against live bulk-policy-intake v1, S81):
// - POST /functions/v1/bulk-policy-intake with { items: [...] }, 1–25 items per
//   request. Larger sets are chunked here.
// - file_url MUST start with `${clinic_id}/` — files are uploaded to
//   `${clinic_id}/imports/` in the policy-documents bucket (storage RLS jails
//   the prefix; the edge function re-validates it — defense in depth).
// - Response is per-item: already_imported:true is a SUCCESS (an idempotent
//   retry that correctly did nothing), never an error.
//
// Retry safety (DDIA — idempotency keys are the batch primitive, not rollback):
// - idempotencyKey is minted ONCE at file-selection time by the caller and
//   NEVER regenerated. Retrying a failed row reuses the same key.
// - uploadedPath is recorded after the first successful storage upload so a
//   retry skips straight to the import call.

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const MAX_ITEMS_PER_REQUEST = 25; // mirrors MAX_ITEMS in bulk-policy-intake v1

export type IntakeFailureCode =
  | 'upload_failed'
  | 'invalid_input'
  | 'denied'
  | 'conversion_failed'
  | 'error';

export interface IntakeRowInput {
  /** Minted once at selection time. NEVER regenerate on retry. */
  idempotencyKey: string;
  file: File;
  /** Set after first successful upload; retries skip re-upload. */
  uploadedPath?: string | null;
  name: string;
  category: string;
  description?: string | null;
  effectiveDate: string; // yyyy-MM-dd
  reviewCadenceMonths?: number | null;
  nextReviewDate: string; // yyyy-MM-dd
  policyOwner: string; // profiles.id
  attestedApprovedAt: string; // yyyy-MM-dd
  requiresAcknowledgement: boolean;
  notes?: string | null;
}

export interface IntakeRowOutcome {
  idempotencyKey: string;
  status: 'imported' | 'already_imported' | 'failed';
  failureCode?: IntakeFailureCode;
  policyId?: string;
  /** Echoed back so the caller can persist it on the row for retry. */
  uploadedPath?: string;
}

type EdgeItemResult =
  | { index: number; idempotency_key: string | null; ok: true; policy_id: string; version_id: string; already_imported: boolean }
  | { index: number; idempotency_key: string | null; ok: false; code: 'invalid_input' | 'denied' | 'conversion_failed' | 'error' };

export type IntakePhase = 'uploading' | 'importing';

/** Storage object names: keep the extension (the edge function's docx
 *  detection is extension-based), strip everything exotic from the rest. */
function sanitizeFileName(name: string): string {
  const lastDot = name.lastIndexOf('.');
  const base = lastDot > 0 ? name.slice(0, lastDot) : name;
  const ext = lastDot > 0 ? name.slice(lastDot + 1).toLowerCase() : '';
  const safeBase = base.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'document';
  return ext ? `${safeBase}.${ext}` : safeBase;
}

export function useBulkPolicyIntake() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [isRunning, setIsRunning] = useState(false);

  /**
   * Uploads (if needed) and imports every row, sequentially by phase.
   * Reports progress via onRowPhase; returns one outcome per row.
   * Never throws for per-row failures — only for total pre-flight failure.
   */
  async function runImport(
    rows: IntakeRowInput[],
    onRowPhase?: (idempotencyKey: string, phase: IntakePhase) => void,
  ): Promise<IntakeRowOutcome[]> {
    if (!profile?.clinic_id) throw new Error('Not authenticated');
    if (rows.length === 0) return [];

    setIsRunning(true);
    const outcomes = new Map<string, IntakeRowOutcome>();

    try {
      // ── Phase 1: upload each file to the clinic's imports prefix ──────
      const uploaded: { row: IntakeRowInput; path: string }[] = [];

      for (const row of rows) {
        if (row.uploadedPath) {
          // Retry path — file is already in storage.
          uploaded.push({ row, path: row.uploadedPath });
          continue;
        }
        onRowPhase?.(row.idempotencyKey, 'uploading');
        const path = `${profile.clinic_id}/imports/${crypto.randomUUID()}_${sanitizeFileName(row.file.name)}`;
        const { error } = await supabase.storage
          .from('policy-documents')
          .upload(path, row.file, { cacheControl: '3600', upsert: false });

        if (error) {
          outcomes.set(row.idempotencyKey, {
            idempotencyKey: row.idempotencyKey,
            status: 'failed',
            failureCode: 'upload_failed',
          });
        } else {
          uploaded.push({ row, path });
        }
      }

      // ── Phase 2: import in chunks of MAX_ITEMS_PER_REQUEST ────────────
      for (let i = 0; i < uploaded.length; i += MAX_ITEMS_PER_REQUEST) {
        const chunk = uploaded.slice(i, i + MAX_ITEMS_PER_REQUEST);
        chunk.forEach(({ row }) => onRowPhase?.(row.idempotencyKey, 'importing'));

        const { data, error } = await supabase.functions.invoke('bulk-policy-intake', {
          body: {
            items: chunk.map(({ row, path }) => ({
              idempotency_key: row.idempotencyKey,
              name: row.name,
              category: row.category,
              description: row.description ?? null,
              effective_date: row.effectiveDate,
              review_cadence_months: row.reviewCadenceMonths ?? null,
              next_review_date: row.nextReviewDate,
              policy_owner: row.policyOwner,
              file_url: path,
              attested_approved_at: row.attestedApprovedAt,
              requires_acknowledgement: row.requiresAcknowledgement,
              notes: row.notes ?? null,
            })),
          },
        });

        if (error || !data?.results) {
          // Whole-request failure (auth, network). Rows stay retryable with
          // their uploadedPath preserved.
          chunk.forEach(({ row, path }) => {
            outcomes.set(row.idempotencyKey, {
              idempotencyKey: row.idempotencyKey,
              status: 'failed',
              failureCode: 'error',
              uploadedPath: path,
            });
          });
          continue;
        }

        for (const result of data.results as EdgeItemResult[]) {
          const match = chunk.find(({ row }) => row.idempotencyKey === result.idempotency_key);
          if (!match) continue;
          if (result.ok) {
            outcomes.set(match.row.idempotencyKey, {
              idempotencyKey: match.row.idempotencyKey,
              status: result.already_imported ? 'already_imported' : 'imported',
              policyId: result.policy_id,
              uploadedPath: match.path,
            });
          } else {
            outcomes.set(match.row.idempotencyKey, {
              idempotencyKey: match.row.idempotencyKey,
              status: 'failed',
              failureCode: result.code,
              uploadedPath: match.path,
            });
          }
        }
      }

      return rows.map(
        (row) =>
          outcomes.get(row.idempotencyKey) ?? {
            idempotencyKey: row.idempotencyKey,
            status: 'failed' as const,
            failureCode: 'error' as const,
          },
      );
    } finally {
      setIsRunning(false);
      queryClient.invalidateQueries({ queryKey: ['policies'] });
    }
  }

  return { runImport, isRunning };
}
